import React, { useRef, useState } from 'react';
import AdminMenu from '../components/AdminMenu';
import Engine from '../tester-widget/engine';
import SampleApp from './testerLab/SampleApp';

/**
 * Admin → Tester Lab.
 *
 * A hands-on harness for the Lyric "Tester Mode" record/replay engine
 * (client/src/tester-widget/engine). Record interactions against either a
 * built-in React sample form or a real deployed app loaded same-origin in an
 * iframe (e.g. /apps/<name>/), then replay them and save/load them as named
 * test cases (persisted in localStorage — no server dependency).
 *
 * This is the integration point the user asked for ("add the work to the Admin
 * menu so I can test the flow"). It is 100% client-side: nothing here needs to
 * be merged into the server to exercise it locally.
 *
 * The engine is framework-agnostic and CommonJS; we default-import it so the
 * whole { Recorder, Replayer, ... } surface comes through webpack's CJS interop.
 */

const { Recorder, Replayer } = Engine;
const LS_KEY = 'lyric.testerLab.cases.v1';
const IGNORE = '[data-lyric-tester-ui]'; // recorder skips the Lab's own chrome

function loadCases() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; }
}
function persistCases(list) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch { /* quota / disabled */ }
}

export default function AdminTesterLab() {
    const [target, setTarget] = useState('sample'); // 'sample' | 'iframe'
    const [iframeUrl, setIframeUrl] = useState('/apps/');
    const [iframeSrc, setIframeSrc] = useState('');
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const [steps, setSteps] = useState([]);
    const [report, setReport] = useState([]);
    const [status, setStatus] = useState('idle');
    const [cases, setCases] = useState(loadCases);
    const [caseName, setCaseName] = useState('');
    const [sampleKey, setSampleKey] = useState(0);

    const recorderRef = useRef(null);
    const iframeRef = useRef(null);
    const fileRef = useRef(null);

    // Resolve the { win, doc } the engine should operate on for the current target.
    function getWinDoc() {
        if (target === 'iframe') {
            const f = iframeRef.current;
            if (!f) throw new Error('iframe not ready');
            let win;
            let doc;
            try {
                win = f.contentWindow;
                doc = f.contentDocument || (win && win.document);
            } catch (e) {
                throw new Error('Cannot access the iframe — it is cross-origin. Load a same-origin app served by Lyric (e.g. /apps/<name>/).');
            }
            if (!doc || !doc.body) throw new Error('The iframe has no accessible document yet. Click "Load app" and wait for it to render.');
            return { win, doc };
        }
        return { win: window, doc: document };
    }

    function reloadIframe() {
        return new Promise((resolve) => {
            const f = iframeRef.current;
            if (!f) return resolve();
            let settled = false;
            const finish = () => { if (settled) return; settled = true; f.removeEventListener('load', finish); resolve(); };
            f.addEventListener('load', finish);
            try {
                if (f.contentWindow) f.contentWindow.location.reload();
                else { const s = f.src; f.src = 'about:blank'; setTimeout(() => { f.src = s; }, 0); }
            } catch {
                const s = f.src; f.src = 'about:blank'; setTimeout(() => { f.src = s; }, 0);
            }
            setTimeout(finish, 4000); // safety net if 'load' never fires
            return undefined;
        });
    }

    async function prepareTarget() {
        if (target === 'iframe') {
            await reloadIframe();
        } else {
            setSampleKey((k) => k + 1); // remount the sample form to a pristine state
            await new Promise((r) => setTimeout(r, 150));
        }
    }

    function startRecording() {
        try {
            const { win, doc } = getWinDoc();
            const rec = new Recorder({ win, doc, ignore: IGNORE });
            rec.start();
            recorderRef.current = rec;
            setSteps([]);
            setReport([]);
            setRecording(true);
            setStatus('recording — interact with the target below…');
        } catch (e) {
            setStatus('cannot record: ' + e.message);
        }
    }

    function stopRecording() {
        const rec = recorderRef.current;
        if (!rec) return;
        const s = rec.stop();
        recorderRef.current = null;
        setSteps(s);
        setRecording(false);
        setStatus(`recorded ${s.length} step(s)`);
    }

    async function replay(stepsToRun) {
        const run = stepsToRun || steps;
        if (!run.length) { setStatus('nothing to replay — record or load a case first'); return; }
        setBusy(true);
        setReport([]);
        setStatus('preparing target…');
        try {
            await prepareTarget();
            const { win, doc } = getWinDoc();
            const replayer = new Replayer({
                win,
                doc,
                timeout: 5000,
                stepDelay: 250,
                onStep: (r) => setStatus(`step ${r.index} · ${r.action} ${r.ok ? '✓' : '✗'}`),
            });
            const rep = await replayer.play(run, { failFast: false });
            setReport(rep);
            const ok = rep.filter((r) => r.ok).length;
            setStatus(`replay complete — ${ok}/${rep.length} step(s) ok`);
        } catch (e) {
            setStatus('replay failed: ' + e.message);
        } finally {
            setBusy(false);
        }
    }

    function resetTarget() {
        prepareTarget();
        setReport([]);
        setStatus('target reset');
    }

    function loadIframeApp() {
        setIframeSrc(iframeUrl);
        setStatus(`loading ${iframeUrl} …`);
    }

    // ---- test cases (localStorage) -----------------------------------------
    function saveCase() {
        if (!steps.length) { setStatus('record something before saving'); return; }
        const name = (caseName || '').trim() || `Case ${new Date().toLocaleString()}`;
        const c = {
            id: 'tc_' + Date.now().toString(36),
            name,
            target,
            iframeUrl: target === 'iframe' ? iframeUrl : undefined,
            steps,
            createdAt: new Date().toISOString(),
        };
        const next = [c, ...cases];
        setCases(next);
        persistCases(next);
        setCaseName('');
        setStatus(`saved "${name}" (${steps.length} steps)`);
    }

    function loadCase(c) {
        setTarget(c.target || 'sample');
        if (c.target === 'iframe' && c.iframeUrl) { setIframeUrl(c.iframeUrl); setIframeSrc(c.iframeUrl); }
        setSteps(c.steps || []);
        setReport([]);
        setStatus(`loaded "${c.name}" (${(c.steps || []).length} steps) — press Replay`);
    }

    function deleteCase(id) {
        const next = cases.filter((c) => c.id !== id);
        setCases(next);
        persistCases(next);
    }

    function exportCases() {
        const blob = new Blob([JSON.stringify(cases, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'lyric-tester-cases.json';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    function importCases(file) {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = JSON.parse(String(reader.result));
                if (!Array.isArray(parsed)) throw new Error('expected an array of cases');
                const byId = new Map(cases.map((c) => [c.id, c]));
                for (const c of parsed) if (c && c.id) byId.set(c.id, c);
                const next = Array.from(byId.values());
                setCases(next);
                persistCases(next);
                setStatus(`imported — ${parsed.length} case(s)`);
            } catch (e) {
                setStatus('import failed: ' + e.message);
            }
        };
        reader.readAsText(file);
    }

    const canRecord = !recording && !busy && (target === 'sample' || !!iframeSrc);

    return (
        <div style={st.page}>
            {/* All chrome is marked [data-lyric-tester-ui] so the recorder ignores
                clicks/inputs on it. The target area below is deliberately NOT marked. */}
            <header data-lyric-tester-ui style={st.header}>
                <div>
                    <h1 style={st.h1}>🧪 Tester Lab</h1>
                    <p style={st.sub}>Record &amp; replay UI test cases with the Lyric tester engine. Client-side only — nothing here touches the server.</p>
                </div>
                <AdminMenu />
            </header>

            <section data-lyric-tester-ui style={st.panel}>
                <div style={st.rowWrap}>
                    <fieldset style={st.targetFs} disabled={recording || busy}>
                        <legend style={st.legend}>Target</legend>
                        <label style={st.radio}>
                            <input type="radio" name="target" checked={target === 'sample'} onChange={() => setTarget('sample')} />
                            Built-in sample form
                        </label>
                        <label style={st.radio}>
                            <input type="radio" name="target" checked={target === 'iframe'} onChange={() => setTarget('iframe')} />
                            Deployed app (iframe)
                        </label>
                    </fieldset>

                    <div style={st.controls}>
                        {!recording ? (
                            <button type="button" onClick={startRecording} disabled={!canRecord} style={{ ...st.btn, ...st.btnRecord, ...(canRecord ? {} : st.btnDisabled) }}>● Record</button>
                        ) : (
                            <button type="button" onClick={stopRecording} style={{ ...st.btn, ...st.btnStop }}>■ Stop</button>
                        )}
                        <button type="button" onClick={resetTarget} disabled={busy || recording} style={{ ...st.btn, ...st.btnGhost, ...((busy || recording) ? st.btnDisabled : {}) }}>↺ Reset target</button>
                        <button type="button" onClick={() => replay()} disabled={busy || recording || steps.length === 0} style={{ ...st.btn, ...st.btnReplay, ...((busy || recording || steps.length === 0) ? st.btnDisabled : {}) }}>▶ Replay</button>
                        <span style={st.status}>{busy && '⏳ '}{status}</span>
                    </div>
                </div>

                {target === 'iframe' && (
                    <div style={st.iframeRow}>
                        <input
                            type="text"
                            value={iframeUrl}
                            onChange={(e) => setIframeUrl(e.target.value)}
                            placeholder="/apps/<app-name>/"
                            style={st.urlInput}
                            disabled={recording}
                        />
                        <button type="button" onClick={loadIframeApp} disabled={recording} style={{ ...st.btn, ...st.btnGhost }}>Load app</button>
                        <span style={st.hint}>Must be same-origin (served by Lyric). Apps with <code>X-Frame-Options: DENY</code> can’t be framed.</span>
                    </div>
                )}
            </section>

            {/* ---- the app-under-test (NOT marked as tester UI) ---- */}
            {target === 'sample' ? (
                <section style={st.targetBox}>
                    <div style={st.targetTag}>app under test — built-in sample</div>
                    <SampleApp key={sampleKey} />
                </section>
            ) : (
                <section data-lyric-tester-ui style={st.targetBox}>
                    <div style={st.targetTag}>app under test — {iframeSrc || '(no app loaded)'}</div>
                    {iframeSrc ? (
                        <iframe
                            ref={iframeRef}
                            title="tester-target"
                            src={iframeSrc}
                            style={st.iframe}
                        />
                    ) : (
                        <div style={st.iframeEmpty}>Enter a path like <code>/apps/your-app/</code> and click <b>Load app</b>.</div>
                    )}
                </section>
            )}

            {/* ---- recorded steps + replay report ---- */}
            <section data-lyric-tester-ui style={st.panel}>
                <div style={st.twoCol}>
                    <div style={st.col}>
                        <h3 style={st.h3}>Recorded steps <span style={st.count}>{steps.length}</span></h3>
                        <pre style={st.pre}>{steps.length ? JSON.stringify(steps, null, 2) : '(none — press Record)'}</pre>
                    </div>
                    <div style={st.col}>
                        <h3 style={st.h3}>Replay report</h3>
                        {report.length === 0 ? (
                            <div style={st.muted}>(no replay yet)</div>
                        ) : (
                            <ul style={st.reportList}>
                                {report.map((r) => (
                                    <li key={r.index} style={{ ...st.reportItem, color: r.ok ? '#0a7d33' : '#b91c1c' }}>
                                        {r.ok ? '✓' : '✗'} #{r.index} {r.action}{r.skipped ? ' (skipped)' : ''}{r.error ? ` — ${r.error}` : ''}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </section>

            {/* ---- saved test cases ---- */}
            <section data-lyric-tester-ui style={st.panel}>
                <h3 style={st.h3}>Saved test cases <span style={st.count}>{cases.length}</span></h3>
                <div style={st.saveRow}>
                    <input
                        type="text"
                        value={caseName}
                        onChange={(e) => setCaseName(e.target.value)}
                        placeholder="Name this recording…"
                        style={st.urlInput}
                    />
                    <button type="button" onClick={saveCase} disabled={steps.length === 0} style={{ ...st.btn, ...st.btnPrimary, ...(steps.length === 0 ? st.btnDisabled : {}) }}>💾 Save current</button>
                    <button type="button" onClick={exportCases} disabled={cases.length === 0} style={{ ...st.btn, ...st.btnGhost, ...(cases.length === 0 ? st.btnDisabled : {}) }}>⬇ Export</button>
                    <button type="button" onClick={() => fileRef.current && fileRef.current.click()} style={{ ...st.btn, ...st.btnGhost }}>⬆ Import</button>
                    <input ref={fileRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={(e) => { importCases(e.target.files[0]); e.target.value = ''; }} />
                </div>

                {cases.length === 0 ? (
                    <div style={st.muted}>No saved cases yet. Record a flow and click “Save current”.</div>
                ) : (
                    <table style={st.table}>
                        <thead>
                            <tr>
                                <th style={st.th}>Name</th>
                                <th style={st.th}>Target</th>
                                <th style={st.th}>Steps</th>
                                <th style={st.th}>Saved</th>
                                <th style={st.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cases.map((c) => (
                                <tr key={c.id}>
                                    <td style={st.td}>{c.name}</td>
                                    <td style={st.td}>{c.target === 'iframe' ? `iframe: ${c.iframeUrl || ''}` : 'sample'}</td>
                                    <td style={st.td}>{(c.steps || []).length}</td>
                                    <td style={st.td}>{new Date(c.createdAt).toLocaleString()}</td>
                                    <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                                        <button type="button" onClick={() => loadCase(c)} style={{ ...st.btnSm, ...st.btnGhost }}>Load</button>
                                        <button type="button" onClick={() => deleteCase(c.id)} style={{ ...st.btnSm, ...st.btnDanger }}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}

const st = {
    page: { maxWidth: 1080, margin: '0 auto', padding: '1.25rem', fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif', color: '#111827' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 },
    h1: { fontSize: 22, margin: '0 0 4px' },
    sub: { margin: 0, color: '#6b7280', fontSize: 13 },
    panel: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.1rem', marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    rowWrap: { display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' },
    targetFs: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.9rem', margin: 0 },
    legend: { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' },
    radio: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, margin: '4px 0' },
    controls: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, flex: 1 },
    btn: { padding: '0.45rem 0.85rem', borderRadius: 7, border: 0, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
    btnSm: { padding: '0.3rem 0.6rem', borderRadius: 6, border: 0, cursor: 'pointer', fontWeight: 600, fontSize: 12, marginRight: 6 },
    btnRecord: { background: '#cf222e', color: '#fff' },
    btnStop: { background: '#6e7781', color: '#fff' },
    btnReplay: { background: '#1f883d', color: '#fff' },
    btnPrimary: { background: '#2563eb', color: '#fff' },
    btnGhost: { background: '#eef2ff', color: '#3730a3' },
    btnDanger: { background: '#fee2e2', color: '#b91c1c' },
    btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
    status: { fontSize: 12, color: '#6b7280', marginLeft: 'auto' },
    iframeRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 12 },
    urlInput: { flex: '1 1 260px', minWidth: 200, padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: 6 },
    hint: { fontSize: 11, color: '#9ca3af', flexBasis: '100%' },
    targetBox: { background: '#fff', border: '2px solid #c7d2fe', borderRadius: 12, padding: '0.5rem', marginBottom: 16, position: 'relative' },
    targetTag: { fontSize: 11, color: '#4338ca', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '2px 4px 8px' },
    iframe: { width: '100%', height: 520, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' },
    iframeEmpty: { padding: '2rem', textAlign: 'center', color: '#6b7280', fontSize: 14 },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    col: { minWidth: 0 },
    h3: { fontSize: 14, margin: '0 0 8px' },
    count: { display: 'inline-block', background: '#eef2ff', color: '#3730a3', borderRadius: 999, padding: '1px 8px', fontSize: 11, marginLeft: 6, fontWeight: 700 },
    pre: { background: '#f6f8fa', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.6rem', margin: 0, maxHeight: 260, overflow: 'auto', fontSize: 12 },
    muted: { color: '#9ca3af', fontSize: 13 },
    reportList: { listStyle: 'none', padding: 0, margin: 0, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 },
    reportItem: { padding: '2px 0' },
    saveRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', borderBottom: '2px solid #e5e7eb', padding: '6px 8px', color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' },
    td: { borderBottom: '1px solid #f0f1f3', padding: '6px 8px', verticalAlign: 'middle' },
};
